import React from 'react';
import {
  Row,
  Col,
  Collapse,
  Descriptions,
  Button,
  Space,
  Card,
  Divider,
  Typography,
  Alert,
} from 'antd';
import { ExclamationCircleFilled, EditOutlined, CheckCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';

import { colors } from '@yuhuinc/ui-library';

import { useWizard } from 'components/shared/Wizard';
import { formatMoney, formatDateTime } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';
import { formatFieldValue } from './helpers/WizardStepHelper';
import ApplicationSidebar from 'components/tenant_app/application_portal/application_sidebar/ApplicationSidebar';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const STEPS_WIZARD_OFFSET = 2;

const propertyText = getTranslatedString('tenantPortal.property');
const unitSuitText = getTranslatedString('tenantPortal.unit.suite');
const monthlyRentText = getTranslatedString('tenantPortal.lease.monthlyRent');
const leaseStartDateText = getTranslatedString('tenantPortal.lease.startDate');
const leaseMoveInDateText = getTranslatedString('tenantPortal.lease.moveInDate');
const leaseEndDateText = getTranslatedString('tenantPortal.lease.endDate');

const DATE_OPTIONS = Object.freeze({
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: null,
  minute: null,
});

const StyledCollapse = styled(Collapse)`
  .ant-collapse-content-box {
    padding: 0;
  }
`;

const PanelHeader = ({ icon, title }) => {
  return (
    <Space size={12}>
      {icon}
      {title}
    </Space>
  );
};

const ApplicationSummary = ({ application, cotenant, blocks, onClickCancel, isPreview }) => {
  const { previousStep, nextStep, goToStep } = useWizard();
  const summaryErrors = blocks?.filter((block) => !block?.form?.valid).map((block) => block.title);

  const getHeader = (block) => {
    const icon =
      block.form.valid || block.fields.length === 0 ? (
        <CheckCircleFilled style={{ color: colors.colors.success }} />
      ) : (
        <ExclamationCircleFilled style={{ color: colors.colors.warning }} />
      );

    return <PanelHeader icon={icon} title={block.title} />;
  };

  const getExtra = (stepIndex) => (
    <EditOutlined
      onClick={(event) => {
        event.stopPropagation();
        goToStep(stepIndex + STEPS_WIZARD_OFFSET);
      }}
    />
  );

  const renderBlockData = () => (
    <StyledCollapse bordered={false} ghost>
      {blocks.map((block) => {
        return (
          <Panel header={getHeader(block)} extra={getExtra(block.step_index)} key={block.id}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {block.form?.errors?.length > 0 && (
                <Alert
                  message={<AlertMessage errors={block.form?.errors} />}
                  type="warning"
                  showIcon
                />
              )}
              {block.values.map((value, index) => (
                <Descriptions bordered size={'small'} column={1} key={`description-${index}`}>
                  {block.rendering_question && (
                    <Descriptions.Item
                      contentStyle={{ width: '50%' }}
                      label={block.rendering_question}
                    >
                      {getTranslatedString(
                        `renderingQuestion.${block.rendering_question_response}`
                      )}
                    </Descriptions.Item>
                  )}

                  {block.fields
                    .filter((field) => field.active)
                    .map((field) => (
                      <Descriptions.Item
                        contentStyle={{ width: '50%' }}
                        key={`description-item-${field.id}`}
                        label={field.name === 'documents' ? 'Documents' : field.label}
                      >
                        {formatFieldValue(
                          value.find((value) => value.field_id === field.id)?.attribute[field.name],
                          field.field_type
                        )}
                      </Descriptions.Item>
                    ))}
                </Descriptions>
              ))}
            </Space>
          </Panel>
        );
      })}
    </StyledCollapse>
  );

  const AlertMessage = ({ errors }) => {
    if (!errors?.length) return null;

    return (
      <>
        <Text>
          {getTranslatedString('tenantPortal.applicationTemplate.applicationSummary.warning')}
        </Text>

        <Paragraph>
          <ul>
            {errors.map((error) => (
              <li>{error}</li>
            ))}
          </ul>
        </Paragraph>
      </>
    );
  };

  return (
    <Row gutter={16}>
      <Col className="gutter-row" xs={{ span: 24, order: 0 }} md={{ span: 8, order: 1 }}>
        <ApplicationSidebar
          applicationWizard
          cotenantId={cotenant.id}
          application={application}
          isPreview={isPreview}
        />
      </Col>

      <Col xs={24} md={0}>
        <Divider />
      </Col>

      <Col className="gutter-row" xs={{ span: 24, order: 1 }} md={{ span: 16, order: 0 }}>
        <Card>
          <Title level={4}>
            {getTranslatedString('tenantPortal.applicationTemplate.applicationSummary.title')}
          </Title>

          <Space direction="vertical">
            <Text>
              {getTranslatedString('tenantPortal.applicationTemplate.applicationSummary.subtitle')}
            </Text>
          </Space>

          {summaryErrors?.length > 0 && (
            <Alert message={<AlertMessage errors={summaryErrors} />} type="warning" showIcon />
          )}
          <Divider />

          <Title level={4}>
            {getTranslatedString(
              'tenantPortal.applicationTemplate.baseApplication.leaseInformation'
            )}
          </Title>

          <Descriptions size={'small'} bordered column={1}>
            <Descriptions.Item contentStyle={{ width: '50%' }} label={propertyText}>
              {application.property.address.street}
            </Descriptions.Item>

            <Descriptions.Item contentStyle={{ width: '50%' }} label={unitSuitText}>
              {application?.unit?.unit_number}
            </Descriptions.Item>

            <Descriptions.Item contentStyle={{ width: '50%' }} label={monthlyRentText}>
              {formatMoney(application.lease.lease_pricing.rent_amount)}
            </Descriptions.Item>

            <Descriptions.Item contentStyle={{ width: '50%' }} label={leaseStartDateText}>
              {formatDateTime(new Date(application.lease.lease_start_date), DATE_OPTIONS)}
            </Descriptions.Item>

            <Descriptions.Item contentStyle={{ width: '50%' }} label={leaseMoveInDateText}>
              {formatDateTime(new Date(application.lease.early_move_in_date), DATE_OPTIONS)}
            </Descriptions.Item>

            <Descriptions.Item contentStyle={{ width: '50%' }} label={leaseEndDateText}>
              {formatDateTime(new Date(application.lease.lease_end_date), DATE_OPTIONS)}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {renderBlockData()}

          <Divider />

          <Space>
            <Button type="primary" disabled={summaryErrors?.length} onClick={nextStep}>
              {getTranslatedString('next')}
            </Button>
            <Button type="default" onClick={previousStep}>
              {getTranslatedString('back')}
            </Button>
            <Button type="default" onClick={onClickCancel}>
              {getTranslatedString('cancel')}
            </Button>
          </Space>
        </Card>
      </Col>
    </Row>
  );
};

export default ApplicationSummary;
